// Copyright (c) Ping. All rights reserved.
//
// Mounts the global search modal in the web shell. The modal itself
// (`@ping/ui/GlobalSearchModal`) is presentation-only — this host
// supplies the four data adapters:
//
//   * `onSearchUsers`  — GET /v1/users/search (canonical directory)
//   * `onSearchLocal`  — Dexie scan over conversations/messages/files
//   * `onStartChatWithUser`  — POST /v1/contacts/lookup + MLS flow
//                               (reuses the NewChatModal orchestration)
//   * `onPickConversation` / `onPickMessage` / `onPickFile` — route
//                               into the chat surface
//
// Opening is controlled by RouterContext: `router.modal.kind === 'search'`.
// Two open paths: TopBar's click-to-open trigger (wired in AppShell)
// and the Cmd/Ctrl+K shell hotkey (same place).

import { useCallback } from 'react';

import {
  type GlobalSearchFile,
  type GlobalSearchLocalResults,
  GlobalSearchModal,
  type GlobalSearchUser,
  useSession,
} from '@ping/ui';

import { log } from '@core/log/log';
import { claimAllDevicesByAccountId, searchUsers } from '@core/network';
import { retrieveIdentity } from '@core/persistence/identity';
import { ensureConversation } from '@features/chat/ensureConversation';
import { getOrCreateMlsConversation } from '@features/chat/mlsConversation';
import { localSearch } from '@features/search/localSearch';
import { getCurrentTransport } from '@session/sessionBootstrap';
import { useRouter } from '@shell/RouterContext';

const CONV_META_PREFIX = 'ping:meta:v1:';

interface ConvMeta {
  inviter: { accountId: string; displayName: string };
  peer: { accountId: string; displayName: string };
}

export function GlobalSearchModalHost(): JSX.Element | null {
  const router = useRouter();
  const { client } = useSession();
  const visible = router.modal.kind === 'search';

  const handleSearchUsers = useCallback(
    async (
      query: string,
      signal: AbortSignal,
    ): Promise<readonly GlobalSearchUser[]> => {
      const trimmed = query.trim();
      if (trimmed.length === 0) return [];
      const res = await searchUsers({ query: trimmed, limit: 20, signal });
      // `DirectoryUser` (from core/network/users.ts) and
      // `GlobalSearchUser` share the same field shape — pass the
      // array through as the wider type the modal expects.
      return res.users as readonly GlobalSearchUser[];
    },
    [],
  );

  const handleSearchLocal = useCallback(
    async (query: string, signal: AbortSignal): Promise<GlobalSearchLocalResults> =>
      localSearch(query, signal),
    [],
  );

  const handleStartChatWithUser = useCallback(
    async (user: GlobalSearchUser): Promise<void> => {
      if (client === null) {
        throw new Error('Still signing you in. Try again in a moment.');
      }
      // `/v1/users/search` only returns `{ account_id, display_name }`
      // — no primary 3PID is exposed, so `POST /v1/contacts/lookup`
      // (which keys off email/phone) can't service this path.
      //
      // `claimAllDevicesByAccountId` now atomically consumes one KP
      // per active device on the target via `POST /v1/contacts/claim-fanout`.
      // Two concurrent inviters receive DISJOINT KP sets (the SQL
      // primitive uses `DISTINCT ON (device_id)` + `FOR UPDATE OF p
      // SKIP LOCKED`), so the recipient's OpenMLS keystore consumes
      // each Welcome's InitKey exactly once — no more "No matching
      // key package was found in the key store" on the second
      // invite.
      const claimed = await claimAllDevicesByAccountId({ accountId: user.id });

      const identity = await retrieveIdentity();

      // Claim our OWN other devices (linked siblings, excluding this one) so the
      // new 1:1 is delivered to every device of the account in the SAME Commit —
      // it then appears on them in realtime, not only after a later link. iOS
      // does this via `claimSelfSiblingDevices()`. Best-effort: a self-claim
      // failure must never block starting the chat.
      const selfSiblingDevices = identity
        ? (
            await claimAllDevicesByAccountId({ accountId: identity.accountId }).catch(
              () => ({ devices: [] as typeof claimed.devices }),
            )
          ).devices
            .filter((d) => d.deviceId !== identity.deviceId)
            .map((d) => ({ deviceIdHex: d.deviceId, keyPackageBytes: d.keyPackage }))
        : [];

      const meta: ConvMeta = {
        inviter: {
          accountId: identity?.accountId ?? '',
          displayName: identity?.displayName ?? 'Someone',
        },
        peer: {
          accountId: claimed.accountId,
          displayName: user.displayName,
        },
      };

      const result = await getOrCreateMlsConversation({
        client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
        transport: getCurrentTransport(),
        beConversationId: `acct:${claimed.accountId}`,
        conversationName: `${CONV_META_PREFIX}${JSON.stringify(meta)}`,
        log,
        peerAccountId: claimed.accountId,
        preClaimedPeers: claimed.devices.map((d) => ({
          deviceIdHex: d.deviceId,
          keyPackageBytes: d.keyPackage,
        })),
        ...(selfSiblingDevices.length > 0 ? { selfSiblingDevices } : {}),
      });
      const localConvId = result.conversationIdHex;

      await ensureConversation({
        conversationId: localConvId,
        counterpartDisplayName: user.displayName,
        kind: 'direct',
        peerAccountId: claimed.accountId,
      });

      if (result.inviteStatus.kind === 'ok') {
        try {
          const profile = {
            kind: 'ping.profile',
            payload: {
              accountId: meta.inviter.accountId,
              displayName: meta.inviter.displayName,
            },
          };
          await result.conv.send(new TextEncoder().encode(JSON.stringify(profile)));
        } catch (profileErr) {
          log.warn({
            event: 'globalSearch.profileIntro.failed',
            error: (profileErr as Error).message,
          });
        }
      }

      log.info({
        event: 'globalSearch.startChat.ok',
        accountId: claimed.accountId,
        inviteStatus: result.inviteStatus.kind,
      });

      // Close the search modal and route into the new conversation.
      // The chat route doesn't (yet) accept a conversationId query
      // param; the chat list re-renders on the new row so the user
      // lands one tap away.
      router.closeModal();
      router.push('/chat');
    },
    [client, router],
  );

  const handlePickConversation = useCallback(
    (_conversationId: string): void => {
      // Same affordance as the People → Start chat path: route to
      // the chat surface; the conversation list will scroll the row
      // into view when conversationId scroll-to is wired.
      router.push('/chat');
    },
    [router],
  );

  const handlePickMessage = useCallback(
    (conversationId: string, _messageId: string): void => {
      // TODO: thread `messageId` through the chat route so the
      // conversation scrolls directly to the matched bubble. For now,
      // route to the chat surface — the user can scroll manually.
      void conversationId;
      router.push('/chat');
    },
    [router],
  );

  const handlePickFile = useCallback(
    (file: GlobalSearchFile): void => {
      // No file-viewer yet — route to the file's conversation so the
      // user can scroll to the message that hosts it.
      void file;
      router.push('/chat');
    },
    [router],
  );

  if (!visible) return null;

  return (
    <GlobalSearchModal
      open={visible}
      onDismiss={router.closeModal}
      onSearchUsers={handleSearchUsers}
      onSearchLocal={handleSearchLocal}
      onStartChatWithUser={handleStartChatWithUser}
      onPickConversation={handlePickConversation}
      onPickMessage={handlePickMessage}
      onPickFile={handlePickFile}
    />
  );
}
